<template>
  <div class="shell">
    <!-- Top menu — mobile-first. On narrow screens the nav collapses
         into a burger that toggles a full-width dropdown panel; on
         wide screens (≥ 640px) the links sit inline next to the
         brand. -->
    <header class="topbar">
      <a class="brand" :href="homepageUrl" :title="t('menuHomepageTitle')">
        <span class="brand-mark">TiBTiX</span>
      </a>

      <button
        class="burger"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        :aria-label="t('menuToggle')"
        @click.stop="toggleMenu"
      >
        <span></span><span></span><span></span>
      </button>

      <nav class="menu" :class="{ open: menuOpen }">
        <a
          class="menu-link"
          href="#"
          @click.prevent="onRegisterClick"
        >{{ t('menuRegister') }}</a>

        <a
          class="menu-link"
          href="#"
          @click.prevent="openTerms"
        >{{ t('menuTerms') }}</a>

        <a
          class="menu-link menu-home"
          :href="homepageUrl"
          target="_blank"
          rel="noopener noreferrer"
          :title="t('menuHomepageTitle')"
          @click="menuOpen = false"
        >{{ t('menuHomepageTitle') }}</a>
      </nav>
    </header>

    <main class="content">
      <!-- „Ausser Betrieb" (blocking): nicht schliessbarer Hinweis statt Login -->
      <div v-if="announceBlocking" class="outage" role="alertdialog" :aria-label="announceTitle">
        <div class="outage-card">
          <h2>{{ announceTitle }}</h2>
          <p v-for="(para, i) in announceBody" :key="i">{{ para }}</p>
        </div>
      </div>
      <LoginOrchestrator
        v-else
        ref="orchestrator"
        :embedded="false"
        :guard-mode="true"
        :initial-email="initialEmail"
        :lock-email="lockEmail"
        :initial-domain="initialDomain"
        :initial-account="initialAccount"
        :initial-connection="initialConnection"
        :locale="locale"
        :redirect-after-login="returnUrl"
        @logged-in="onLoggedIn"
        @close="reopenAnnounce"
      />
    </main>

    <!-- Full-screen terms modal. On mobile the modal fills the viewport;
         on desktop it caps at a readable width but is still full-height
         per the user's "alle popups voll screen" requirement. -->
    <div v-if="termsOpen" class="modal-backdrop" @click.self="closeTerms">
      <div class="modal" role="dialog" :aria-label="t('termsTitle')">
        <header class="modal-header">
          <h3>{{ termsHeaderTitle }}</h3>
          <button class="close-btn" @click="closeTerms" :aria-label="t('close')">&times;</button>
        </header>
        <div class="modal-body">
          <div v-if="termsLoading" class="loading">{{ t('loading') }}</div>
          <div v-else-if="termsError" class="error-message">{{ termsError }}</div>
          <template v-else-if="terms">
            <div class="terms-meta-row">
              <div class="terms-meta" v-if="terms.version">
                {{ activeLang ? activeLang.versionLabel : t('termsVersionLabel') }}:
                <strong>{{ terms.version }}</strong>
              </div>
              <div v-if="hasMultipleLangs" class="lang-toggle">
                <button
                  v-for="code in availableLangs"
                  :key="code"
                  class="lang-btn"
                  :class="{ active: termsLang === code }"
                  @click="termsLang = code"
                >{{ code.toUpperCase() }}</button>
              </div>
            </div>
            <div class="terms-body">
              <p v-for="(para, i) in paragraphs" :key="i">{{ para }}</p>
              <div v-if="paragraphs.length === 0" class="empty">
                {{ t('termsLoadFailed') }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Vorgelagerter Info-Hinweis: kompakter modaler Dialog (Logo oben, Checkbox „nicht mehr anzeigen") -->
    <div v-if="announceInfoVisible" class="announce-backdrop" @click.self="closeAnnounce">
      <div class="announce-card" role="dialog" :aria-label="announceTitle">
        <img class="announce-logo" :src="logoUrl" alt="TiBTiX" />
        <h3 class="announce-title">{{ announceTitle }}</h3>
        <div class="announce-text">
          <p v-for="(para, i) in announceBody" :key="i">{{ para }}</p>
        </div>
        <label class="announce-check">
          <input type="checkbox" v-model="announceDontShow" />
          <span>{{ t('announceDismiss') }}</span>
        </label>
        <button class="announce-primary" @click="closeAnnounce">{{ t('announceOk') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import LoginOrchestrator from './LoginOrchestrator.vue'
import LoginApiService from './services/LoginApiService.js'
import { useI18n, getLocale } from './i18n/index.js'

// Public TiBTiX marketing site. Hard-coded for now — could move to a
// build-time env if we ever need to point this at a staging host.
const HOMEPAGE_URL = 'https://www.tibtix.com'

// Stabiler Inhalts-Hash (FNV-1a, 8 Hex) — „nicht mehr anzeigen" wird am HASH der Nachricht gemerkt, nicht an einer
// id. Aendert sich der Inhalt (Text/Modus/…), aendert sich der Hash -> der Hinweis erscheint wieder bei allen.
function announceHashOf(obj) {
  let s
  try { s = JSON.stringify(obj) } catch (e) { s = String(obj) }
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return ('0000000' + h.toString(16)).slice(-8)
}

export default defineComponent({
  name: 'StandaloneShell',

  components: { LoginOrchestrator },

  props: {
    returnUrl: { type: String, required: true },
    initialEmail: { type: String, default: '' },
    lockEmail: { type: Boolean, default: false },
    // Deep-link hints (e.g. from a notification email): preselect an admin
    // domain or a client connection after the discovery login.
    initialDomain: { type: String, default: '' },
    initialAccount: { type: String, default: '' },
    initialConnection: { type: String, default: '' },
    locale: { type: String, default: '' }
  },

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      homepageUrl: HOMEPAGE_URL,
      menuOpen: false,
      termsOpen: false,
      termsLoading: false,
      termsError: '',
      // Server payload shape: { version, languages: { de: {title, body, versionLabel, ...}, en: {...} } }
      terms: null,
      termsLang: 'de',
      // Vorgelagerter Hinweis aus Resources: { id, enabled, mode, de/en:{title,body[]} }
      announce: null,
      announceClosed: false,
      announceForced: false,
      announceDontShow: false,
      // Laufzeit-URL (nicht vom Build als Asset aufloesen lassen -> gebunden statt statisch)
      logoUrl: '/tt/resources/images/about/TIBTIX_LOGO.png'
    }
  },

  computed: {
    availableLangs() {
      if (!this.terms || !this.terms.languages) return []
      return Object.keys(this.terms.languages)
    },
    hasMultipleLangs() {
      return this.availableLangs.length > 1
    },
    /** Active per-language bundle: { title, body, versionLabel, acceptCheckbox, readLink, required }. */
    activeLang() {
      if (!this.terms || !this.terms.languages) return null
      return this.terms.languages[this.termsLang]
        || this.terms.languages.en
        || this.terms.languages[this.availableLangs[0]]
        || null
    },
    termsHeaderTitle() {
      return (this.activeLang && this.activeLang.title) || this.t('termsTitle')
    },
    /** Split the body text on blank-line boundaries so each clause renders
     *  as its own paragraph (with pre-wrap for in-paragraph line breaks). */
    paragraphs() {
      const body = this.activeLang && this.activeLang.body
      if (!body) return []
      return String(body).split(/\n\n+/).map(s => s.trim()).filter(Boolean)
    },

    /** Sprach-Bundle des Hinweises (de/en), Fallback en -> de. */
    announceBundle() {
      const a = this.announce
      if (!a) return null
      const ui = (this.locale || getLocale() || 'de').toLowerCase().slice(0, 2)
      return a[ui] || a.en || a.de || null
    },
    announceActive() {
      return !!(this.announce && this.announce.enabled === true && this.announceBundle)
    },
    /** Hash NUR des angezeigten Textes (Titel + Text, DE + EN) — Schluessel fuer „nicht mehr anzeigen".
     *  Bewusst OHNE id/enabled/mode: „nicht mehr anzeigen" bleibt unterdrueckt, solange der TEXT gleich bleibt;
     *  aendert sich der Text (in irgendeiner Sprache), aendert sich der Hash und der Hinweis erscheint wieder. */
    announceHash() {
      const a = this.announce
      if (!a) return ''
      const parts = []
      for (const lang of ['de', 'en']) {
        const b = a[lang]
        if (b) parts.push(lang, String(b.title || ''), JSON.stringify(b.body || ''))
      }
      return announceHashOf(parts.join('\u0001'))
    },
    announceTitle() {
      return (this.announceBundle && this.announceBundle.title) || ''
    },
    announceBody() {
      const b = this.announceBundle && this.announceBundle.body
      if (Array.isArray(b)) return b.map(s => String(s)).filter(Boolean)
      return b ? String(b).split(/\n\n+/).map(s => s.trim()).filter(Boolean) : []
    },
    /** „ausser Betrieb": nicht schliessbar, verhindert das Login. */
    announceBlocking() {
      return this.announceActive && this.announce.mode === 'blocking'
    },
    /** Info-Hinweis sichtbar: aktiv, nicht blockierend, diese Runde nicht geschlossen und nicht lokal unterdrueckt.
     *  Test-Trick: ?announce=1 in der URL erzwingt die Anzeige (ignoriert „nicht mehr anzeigen"). */
    announceInfoVisible() {
      if (!this.announceActive || this.announce.mode === 'blocking') return false
      if (this.announceForced) return true          // per „X" (oder ?announce=1) erneut geoeffnet
      if (this.announceClosed) return false          // in dieser Sitzung geschlossen
      let paramForced = false
      try { paramForced = /[?&]announce=1(&|$)/.test(window.location.search) } catch (e) { paramForced = false }
      if (paramForced) return true
      let dismissed = ''
      try { dismissed = localStorage.getItem('tt.announce.dismissed') || '' } catch (e) { dismissed = '' }
      return dismissed !== this.announceHash
    }
  },

  mounted() {
    // Single, persistent doc-level closer. Survives multiple
    // open/close cycles without re-registering — the watcher pattern
    // we had before could end up registering multiple closers and
    // race with the click that just opened the menu.
    this._docClickCloser = (e) => {
      if (!this.menuOpen) return
      // Skip clicks on the burger (handled by toggleMenu via
      // @click.stop) or inside the dropdown itself (menu-link handlers
      // close it explicitly). Everything else closes the panel.
      const root = this.$el
      if (!root) return
      const burgerEl = root.querySelector('.burger')
      const menuEl = root.querySelector('.menu')
      if (burgerEl && burgerEl.contains(e.target)) return
      if (menuEl && menuEl.contains(e.target)) return
      this.menuOpen = false
    }
    document.addEventListener('click', this._docClickCloser)

    // Vorgelagerten Hinweis aus den Resources laden (ohne Anmeldung; Fehler/leer = kein Dialog).
    LoginApiService.announcement().then(data => {
      if (data && typeof data === 'object') this.announce = data
    }).catch(() => { /* kein Hinweis */ })

    // Browser-"Zurueck" soll den (nur fuer die Sitzung geschlossenen) Info-Dialog wieder zeigen — auch aus dem
    // Back-Forward-Cache (persisted), wo Vue-Zustand eingefroren restauriert wird. „Nicht mehr anzeigen" bleibt
    // wirksam (das haengt an localStorage, nicht an diesem Flag).
    this._announcePageShow = () => { this.announceClosed = false }
    window.addEventListener('pageshow', this._announcePageShow)
  },

  beforeUnmount() {
    if (this._docClickCloser) {
      document.removeEventListener('click', this._docClickCloser)
      this._docClickCloser = null
    }
    if (this._announcePageShow) {
      window.removeEventListener('pageshow', this._announcePageShow)
      this._announcePageShow = null
    }
  },

  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen
    },

    /** Dialog schliessen (Zum Login). Ist die Checkbox „nicht mehr anzeigen" gesetzt, wird genau dieser Hinweis
     *  (id) lokal pro Geraet unterdrueckt; sonst erscheint er beim naechsten Mal wieder. */
    closeAnnounce() {
      if (this.announceDontShow && this.announce) {
        try { localStorage.setItem('tt.announce.dismissed', this.announceHash) } catch (e) { /* privat/gesperrt */ }
      }
      this.announceClosed = true
      this.announceForced = false
    },
    /** Das (sonst funktionslose) „X" im Login zeigt den Hinweis erneut (nur wenn es einen gibt). */
    reopenAnnounce() {
      if (!this.announceActive) return
      this.announceDontShow = false
      this.announceClosed = false
      this.announceForced = true
    },

    onRegisterClick() {
      this.menuOpen = false
      const o = this.$refs.orchestrator
      if (o && typeof o.goToRegister === 'function') o.goToRegister()
    },

    async openTerms() {
      this.menuOpen = false
      this.termsOpen = true
      if (this.terms) return
      this.termsLoading = true
      this.termsError = ''
      try {
        const data = await LoginApiService.getTerms()
        if (!data) {
          this.termsError = this.t('termsLoadFailed')
          return
        }
        this.terms = data
        const ui = (this.locale || getLocale() || 'de').toLowerCase()
        const langs = data.languages ? Object.keys(data.languages) : []
        this.termsLang = langs.includes(ui) ? ui : (langs[0] || 'de')
      } catch (e) {
        this.termsError = this.t('termsLoadFailed')
      } finally {
        this.termsLoading = false
      }
    },

    closeTerms() {
      this.termsOpen = false
    },

    onLoggedIn(_session, target) {
      // The orchestrator now emits the resolved target (picker-chosen
      // connection URL OR the generic `?return=` URL) and triggers
      // the navigation itself. If — for whatever reason — it didn't
      // produce a target, fall back to the host-supplied returnUrl
      // so the user doesn't get stuck on the login page.
      if (target) return
      if (this.returnUrl && window.location.href !== this.returnUrl) {
        window.location.assign(this.returnUrl)
      }
    }
  }
})
</script>

<style scoped>
/* ============================================================
   Mobile-first base — phones and small viewports use these
   defaults. The @media (min-width:640px) block adds the
   desktop overrides on top.
   ============================================================ */

.shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f3f4f6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.topbar {
  position: relative;
  z-index: 150;     /* stack above the content area so the absolute-
                       positioned menu dropdown below isn't occluded
                       by the login-card's stacking context. */
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #111827;
}
.brand-mark {
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: #2563eb;
}

/* ---- Burger ----
   No "X" transformation — the user wants the burger to stay a burger
   even while the menu is open. The single, persistent doc-click
   listener in `mounted()` closes the panel; clicking the burger
   itself toggles it via `toggleMenu()`. */
.burger {
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  width: 36px;
  height: 28px;
  padding: 4px 0;
  border: none;
  background: none;
  cursor: pointer;
}
.burger span {
  display: block;
  height: 3px;
  background: #374151;
  border-radius: 1px;
}

/* ---- Nav (mobile = full-width dropdown panel below the topbar) ----
   Anchored to the topbar via position:absolute + top:100%; the
   topbar's z-index ensures we're above the content area. */
.menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: none;
  flex-direction: column;
  padding: 8px 0;
  box-shadow: 0 6px 16px rgba(0,0,0,0.06);
}
.menu.open { display: flex; }

.menu-link {
  padding: 14px 16px;
  text-decoration: none;
  color: #374151;
  font-size: 16px;
  border-bottom: 1px solid #f3f4f6;
}
.menu-link:last-child { border-bottom: none; }
.menu-link:hover { background: #f3f4f6; color: #111827; }
.menu-home { color: #2563eb; }

/* Mobile: zero padding so the login-card / pickers actually fill the
   viewport. Desktop adds breathing room. */
.content {
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 0;
}

/* ============================================================
   Modal — full-screen by default (mobile-first), capped width on
   wide screens but still full viewport height.
   ============================================================ */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 200;
}
.modal {
  background: white;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.modal-header h3 { margin: 0; font-size: 1.1rem; }
.close-btn {
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 8px;
}
.close-btn:hover { color: #000; }

.modal-body {
  padding: 14px 16px;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
}
.loading { text-align: center; color: #6b7280; padding: 24px 0; }

.terms-meta-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #6b7280;
}
.lang-toggle { display: inline-flex; gap: 6px; }
.lang-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: white;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.lang-btn.active { background: #2563eb; color: white; border-color: #2563eb; }

.terms-body {
  font-size: 15px;
  line-height: 1.55;
  color: #1f2937;
}
.terms-body p {
  margin: 0 0 12px 0;
  white-space: pre-wrap;
}
.terms-body p:last-child { margin-bottom: 0; }
.terms-body .empty { color: #6b7280; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

/* ============================================================
   Desktop overrides — kicks in on ≥640px (tablet+).
   ============================================================ */
@media (min-width: 640px) {
  /* Kopfinhalt so breit wie die Anmeldekarte (460px + 2×24px Innenrand), Hintergrund über die ganze Breite —
     wie im neuen UI (/tt/ui: Titelbalken = Listenbreite) */
  /* rechts minus 12px: der Innenrand der Menü-Links, damit deren Text mit der Karte abschliesst */
  .topbar { padding: 12px max(12px, calc((100% - 508px) / 2 - 12px)) 12px max(24px, calc((100% - 508px) / 2)); }
  .burger { display: none; }

  .menu {
    position: static;
    display: flex !important;
    flex-direction: row;
    gap: 4px;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }
  .menu-link {
    padding: 8px 12px;
    font-size: 14px;
    border-bottom: none;
    border-radius: 6px;
  }

  .content { padding: 40px 16px; }
  .modal-backdrop {
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .modal {
    width: min(720px, 100%);
    height: 100vh;
    height: 100dvh;
  }
}

/* Vorgelagerter Info-Dialog: kompaktes, zentriertes Modal mit abgerundetem Rand (mobile-first) */
.announce-backdrop {
  position: fixed;
  inset: 0;
  z-index: 300;           /* ueber Login-Overlay (100), Topbar (150) und AGB-Modal (200) */
  background: rgba(14, 16, 30, .5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.announce-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border: 1px solid #e0e3ef;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(14, 16, 30, .25);
  padding: 24px 22px;
  text-align: center;
}
.announce-logo { display: block; margin: 0 auto 14px; max-height: 40px; max-width: 70%; height: auto; }
.announce-title { margin: 0 0 12px; font-size: 1.15rem; font-weight: 700; }
.announce-text { text-align: center; }
.announce-text p { margin: 0 0 10px; color: #4a5160; line-height: 1.5; }
.announce-text p:last-child { margin-bottom: 0; }
.announce-check {
  display: flex;
  align-items: center;
  gap: 9px;
  justify-content: center;
  margin: 18px 0 16px;
  font-size: .9rem;
  color: #334;
  cursor: pointer;
  user-select: none;
}
.announce-check input { width: 17px; height: 17px; cursor: pointer; accent-color: #4f46e5; }
.announce-primary {
  width: 100%;
  padding: 13px 16px;
  border: 0;
  border-radius: 10px;
  background: #4f46e5;
  color: #fff;
  font-family: inherit;
  font-size: .98rem;
  font-weight: 600;
  cursor: pointer;
}
.announce-primary:hover { filter: brightness(1.05); }
/* Smartphone: Dialog deckt fast alles ab (nur ein kleiner Rand, Ecken bleiben abgerundet) */
@media (max-width: 560px) {
  .announce-backdrop { padding: 12px; }
  .announce-card {
    max-width: none;
    width: 100%;
    min-height: calc(100dvh - 24px);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .announce-logo { max-height: 48px; }
}

/* „Ausser Betrieb": zentrierter, nicht schliessbarer Hinweis statt Login */
.outage {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}
.outage-card {
  max-width: 480px;
  width: 100%;
  background: #fff;
  border: 1px solid #e0e3ef;
  border-radius: 12px;
  padding: 22px 20px;
  text-align: center;
}
.outage-card h2 { margin: 0 0 12px; font-size: 1.2rem; }
.outage-card p { margin: 0 0 10px; color: #4a5160; line-height: 1.5; }
.outage-card p:last-child { margin-bottom: 0; }
</style>

<style>
/* Standalone-Seite: kein Browser-Standardrand um die Schale (sonst 8px weisser Rand) */
html, body { margin: 0; }
</style>
